import { Link } from "@tanstack/react-router";
import { SEO } from "@/components/SEO";

// Beginner guides - for users who don't know what DMARC is
const beginnerGuides = [
  {
    title: "Why are my emails going to spam?",
    description: "The #1 reason business emails end up in spam folders - and how to fix it.",
    href: "/guides/why-emails-go-to-spam",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    title: "What is email authentication?",
    description: "A plain-English explanation of SPF, DKIM, and DMARC - no technical jargon.",
    href: "/guides/what-is-email-authentication",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9.879 7.519c1.171-1.025 3.071-1.025 4.242 0 1.172 1.025 1.172 2.687 0 3.712-.203.179-.43.326-.67.442-.745.361-1.45.999-1.45 1.827v.75M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9 5.25h.008v.008H12v-.008z" />
      </svg>
    ),
  },
  {
    title: "Protect your business from email spoofing",
    description: "Learn how scammers impersonate your domain and how to stop them.",
    href: "/guides/protect-from-spoofing",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
      </svg>
    ),
  },
];

// Step-by-step setup guide
const setupSteps = [
  {
    step: 1,
    title: "Check your current email health",
    description: "Use our free domain checker to see your current SPF, DKIM, and DMARC status. No account needed.",
    action: "Check Your Domain",
    actionHref: "/tools",
  },
  {
    step: 2,
    title: "Create your GetSent account",
    description: "Sign up for free and add your domain. We'll generate a unique verification code for you.",
    action: "Sign Up Free",
    actionHref: "/register",
  },
  {
    step: 3,
    title: "Add a DNS record to verify ownership",
    description: "Copy the TXT record we provide and add it to your domain's DNS settings. We'll walk you through it step by step.",
    action: "See DNS Guide",
    actionHref: "#dns-guide",
  },
  {
    step: 4,
    title: "Set up your DMARC policy",
    description: "Start with monitoring mode to see who's sending email as your domain, then gradually increase protection.",
    action: "DMARC Guide",
    actionHref: "#dmarc-guide",
  },
];

// Technical documentation for intermediate/advanced users
const technicalGuides = [
  {
    category: "SPF Configuration",
    items: [
      {
        title: "SPF record syntax explained",
        description: "A complete guide to SPF mechanisms, qualifiers, and modifiers.",
        href: "#",
      },
      {
        title: "Staying under the 10 DNS lookup limit",
        description: "Best practices for optimizing your SPF record and avoiding lookup failures.",
        href: "#",
      },
      {
        title: "Common SPF mistakes to avoid",
        description: "Learn from the most frequent SPF configuration errors we see.",
        href: "#",
      },
    ],
  },
  {
    category: "DKIM Configuration",
    items: [
      {
        title: "How DKIM signing works",
        description: "Understanding public/private key cryptography for email authentication.",
        href: "#",
      },
      {
        title: "Setting up DKIM with popular ESPs",
        description: "Guides for Google Workspace, Microsoft 365, SendGrid, and more.",
        href: "#",
      },
      {
        title: "Rotating DKIM keys safely",
        description: "How to rotate your DKIM keys without disrupting email delivery.",
        href: "#",
      },
    ],
  },
  {
    category: "DMARC Policy",
    items: [
      {
        title: "DMARC policy progression",
        description: "How to safely move from p=none to p=quarantine to p=reject.",
        href: "#",
      },
      {
        title: "Understanding DMARC reports",
        description: "How to read and interpret the aggregate reports you receive.",
        href: "#",
      },
      {
        title: "Handling third-party senders",
        description: "How to maintain DMARC compliance when using external email services.",
        href: "#",
      },
    ],
  },
  {
    category: "DNS Guides",
    items: [
      {
        title: "Adding DNS records with GoDaddy",
        description: "Step-by-step guide for GoDaddy users to add TXT records.",
        href: "#",
      },
      {
        title: "Adding DNS records with Cloudflare",
        description: "Step-by-step guide for Cloudflare users to add TXT records.",
        href: "#",
      },
      {
        title: "Adding DNS records with Namecheap",
        description: "Step-by-step guide for Namecheap users to add TXT records.",
        href: "#",
      },
    ],
  },
  {
    category: "Website Security",
    items: [
      {
        title: "Website Security & Email Deliverability",
        description: "How your website's security posture affects email deliverability to enterprise recipients.",
        href: "/guides/website-security-email-deliverability",
      },
      {
        title: "Fixing Security Headers",
        description: "Step-by-step guides for Cloudflare, Vercel, AWS, Netlify, Nginx, Apache, and more.",
        href: "/guides/fixing-security-headers",
      },
      {
        title: "Understanding Email Security Gateways",
        description: "How enterprise systems like Proofpoint, Mimecast, and Barracuda affect your email delivery.",
        href: "/guides/recipient-security-gateways",
      },
    ],
  },
];

const quickLinks = [
  {
    title: "Free Tools",
    description: "Use our free SPF, DKIM, and DMARC lookup and generator tools.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
      </svg>
    ),
    href: "/tools",
  },
  {
    title: "Video Tutorials",
    description: "Coming soon - step-by-step video guides for common tasks.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.348a1.125 1.125 0 010 1.971l-11.54 6.347a1.125 1.125 0 01-1.667-.985V5.653z" />
      </svg>
    ),
    href: "#",
    comingSoon: true,
  },
  {
    title: "Blog",
    description: "Stay up to date with email security news, tips, and best practices.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 7.5h1.5m-1.5 3h1.5m-7.5 3h7.5m-7.5 3h7.5m3-9h3.375c.621 0 1.125.504 1.125 1.125V18a2.25 2.25 0 01-2.25 2.25M16.5 7.5V18a2.25 2.25 0 002.25 2.25M16.5 7.5V4.875c0-.621-.504-1.125-1.125-1.125H4.125C3.504 3.75 3 4.254 3 4.875V18a2.25 2.25 0 002.25 2.25h13.5M6 7.5h3v3H6v-3z" />
      </svg>
    ),
    href: "/blog",
  },
  {
    title: "Contact Support",
    description: "Get help from our team via email.",
    icon: (
      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
      </svg>
    ),
    href: "mailto:support@getsent.dev",
  },
];

export function ResourcesPage() {
  return (
    <div>
      <SEO
        title="Help Center & Resources"
        description="Learn about DMARC, SPF, and DKIM email authentication. Guides, tutorials, and best practices for protecting your domain from email spoofing."
        canonical="/resources"
      />
      {/* Hero */}
      <section className="relative overflow-hidden border-b bg-gradient-to-b from-indigo-50/50 to-transparent py-16 dark:from-indigo-950/20 sm:py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl text-center">
            <h1 className="text-4xl font-bold tracking-tight sm:text-5xl">
              Help{" "}
              <span className="bg-gradient-to-r from-indigo-500 to-purple-600 bg-clip-text text-transparent">
                Center
              </span>
            </h1>
            <p className="mt-4 text-lg text-muted-foreground">
              Everything you need to master email authentication and get the most out of GetSent.
            </p>
          </div>

          {/* Support callout */}
          <div className="mx-auto mt-10 max-w-2xl">
            <div className="rounded-2xl border border-indigo-200 bg-white/80 p-6 shadow-sm backdrop-blur dark:border-indigo-800 dark:bg-slate-900/80">
              <div className="flex flex-col items-center gap-4 text-center sm:flex-row sm:text-left">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-indigo-100 dark:bg-indigo-950">
                  <svg className="h-6 w-6 text-indigo-600 dark:text-indigo-400" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8.625 12a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H8.25m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0H12m4.125 0a.375.375 0 11-.75 0 .375.375 0 01.75 0zm0 0h-.375M21 12c0 4.556-4.03 8.25-9 8.25a9.764 9.764 0 01-2.555-.337A5.972 5.972 0 015.41 20.97a5.969 5.969 0 01-.474-.065 4.48 4.48 0 00.978-2.025c.09-.457-.133-.901-.467-1.226C3.93 16.178 3 14.189 3 12c0-4.556 4.03-8.25 9-8.25s9 3.694 9 8.25z" />
                  </svg>
                </div>
                <div className="flex-1">
                  <h2 className="font-semibold">Need help with email deliverability?</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Sign up for free and reach out to our support team. We'll help you figure out why your emails aren't getting delivered.
                  </p>
                </div>
                <Link
                  to="/register"
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-500"
                >
                  Get Started Free
                  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                  </svg>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* New to email authentication? */}
      <section className="py-12 border-b">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
            </svg>
            New to email authentication?
          </div>
          <h2 className="mt-2 text-2xl font-bold">Start Here</h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Not sure why you're here? These guides explain email deliverability in plain English -
            no technical background required.
          </p>
          <div className="mt-8 grid gap-4 sm:grid-cols-3">
            {beginnerGuides.map((guide) => (
              <Link
                key={guide.title}
                to={guide.href}
                className="group rounded-xl border bg-card p-6 transition-all hover:border-indigo-500/50 hover:shadow-lg"
              >
                <div className="inline-flex rounded-lg bg-indigo-100 p-3 text-indigo-600 dark:bg-indigo-950 dark:text-indigo-400">
                  {guide.icon}
                </div>
                <h3 className="mt-4 font-semibold group-hover:text-indigo-600 dark:group-hover:text-indigo-400">
                  {guide.title}
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  {guide.description}
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Step-by-step setup */}
      <section className="py-12 bg-muted/30">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Get Set Up in 4 Steps</h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Follow this guide to protect your domain. Most businesses complete setup in under 30 minutes.
          </p>
          <div className="mt-8 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {setupSteps.map((step) => (
              <div
                key={step.step}
                className="relative rounded-xl border bg-card p-6"
              >
                <div className="absolute -top-3 left-4 flex h-6 w-6 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 text-xs font-bold text-white">
                  {step.step}
                </div>
                <h3 className="mt-2 font-semibold">{step.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">
                  {step.description}
                </p>
                <Link
                  to={step.actionHref}
                  className="mt-4 inline-flex text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                >
                  {step.action} →
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quick links */}
      <section className="py-12">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Resources</h2>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {quickLinks.map((link) => {
              const content = (
                <>
                  <div className={`inline-flex rounded-lg p-3 text-white ${link.comingSoon ? 'bg-gray-400' : 'bg-gradient-to-br from-indigo-500 to-purple-600'}`}>
                    {link.icon}
                  </div>
                  <h3 className="mt-4 font-semibold flex items-center gap-2">
                    {link.title}
                    {link.comingSoon && (
                      <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                        Coming soon
                      </span>
                    )}
                  </h3>
                  <p className="mt-1 text-sm text-muted-foreground">
                    {link.description}
                  </p>
                </>
              );

              if (link.comingSoon) {
                return (
                  <div
                    key={link.title}
                    className="rounded-xl border bg-card p-6 opacity-75 cursor-default"
                  >
                    {content}
                  </div>
                );
              }

              return (
                <Link
                  key={link.title}
                  to={link.href}
                  className="group rounded-xl border bg-card p-6 transition-all hover:border-indigo-500/50 hover:shadow-lg hover:shadow-indigo-500/5"
                >
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Blog Posts */}
      <section className="py-12 border-t">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold">Popular Guides</h2>
              <p className="mt-1 text-muted-foreground">Step-by-step tutorials for common email authentication tasks.</p>
            </div>
            <Link to="/blog" className="text-sm font-medium text-indigo-600 hover:text-indigo-500">
              View all posts →
            </Link>
          </div>
          <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Link
              to="/blog/$slug"
              params={{ slug: "how-to-check-dmarc-setup" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">How to Check Your DMARC Setup</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">Step-by-step guide with our free tool.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "free-spf-checker" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Free SPF Checker</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">Validate your SPF record in seconds.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "complete-guide-dmarc-2026" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Guide</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Complete Guide to DMARC</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">Everything you need to know about DMARC.</p>
            </Link>
            <Link
              to="/blog/$slug"
              params={{ slug: "analyzing-dmarc-reports" }}
              className="group rounded-xl border bg-card p-5 transition-all hover:border-indigo-500/50 hover:shadow-lg"
            >
              <div className="text-xs font-medium text-indigo-600 mb-2">Technical</div>
              <h3 className="font-semibold group-hover:text-indigo-600">Analyzing DMARC Reports</h3>
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">Learn to interpret your report data.</p>
            </Link>
          </div>
        </div>
      </section>

      {/* Technical Documentation */}
      <section className="py-12 sm:py-16 border-t">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="flex items-center gap-2 text-sm font-medium text-purple-600 dark:text-purple-400">
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
            </svg>
            Technical Documentation
          </div>
          <h2 className="mt-2 text-2xl font-bold">Deep Dive Guides</h2>
          <p className="mt-2 text-muted-foreground max-w-2xl">
            Detailed technical documentation for IT administrators and developers.
          </p>
          <div className="mt-8 grid gap-8 lg:grid-cols-2">
            {technicalGuides.map((section) => (
              <div key={section.category}>
                <h3 className="flex items-center gap-2 text-lg font-semibold">
                  <span className="h-1.5 w-1.5 rounded-full bg-purple-500" />
                  {section.category}
                </h3>
                <div className="mt-4 divide-y rounded-xl border bg-card">
                  {section.items.map((item) => (
                    <a
                      key={item.title}
                      href={item.href}
                      className="block p-4 transition-colors hover:bg-muted/50"
                    >
                      <div className="font-medium">{item.title}</div>
                      <p className="mt-1 text-sm text-muted-foreground">
                        {item.description}
                      </p>
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t bg-muted/30 py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <h2 className="text-2xl font-bold">Frequently Asked Questions</h2>
          <div className="mt-8 grid gap-6 lg:grid-cols-2">
            {[
              {
                q: "I'm not technical. Can I still use GetSent?",
                a: "Absolutely! Our setup wizard walks you through each step with clear instructions. You'll need to copy and paste some text into your DNS settings - we show you exactly what to do with screenshots for popular providers like GoDaddy, Cloudflare, and Namecheap.",
              },
              {
                q: "Will this help my emails reach customers' inboxes?",
                a: "Yes! Email providers like Gmail and Outlook check for SPF, DKIM, and DMARC records. Having these properly configured tells them your emails are legitimate, which significantly improves deliverability and reduces the chance of landing in spam.",
              },
              {
                q: "What is DNS and why do I need to change it?",
                a: "DNS (Domain Name System) is like your domain's phonebook - it tells the internet where to find your website and email. Adding authentication records to your DNS proves you own the domain and authorize certain servers to send email on your behalf. It's safe and won't affect your website.",
              },
              {
                q: "How long does setup take?",
                a: "Most people complete the initial setup in 15-30 minutes. After adding your DNS records, it can take up to 48 hours for changes to fully propagate across the internet, but often it's much faster.",
              },
              {
                q: "How long does it take to start receiving reports?",
                a: "After configuring your DMARC record with our reporting address, you'll typically start receiving reports within 24-48 hours, depending on the email providers sending to your domain.",
              },
              {
                q: "Can I use GetSent with multiple domains?",
                a: "Yes! Depending on your plan, you can monitor multiple domains from a single dashboard. Our Pro and Business plans support 25 and 100 domains respectively.",
              },
            ].map((faq) => (
              <div key={faq.q} className="rounded-xl border bg-card p-6">
                <h3 className="font-semibold">{faq.q}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-16">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="rounded-3xl bg-gradient-to-r from-indigo-500 to-purple-600 px-6 py-16 text-center shadow-2xl sm:px-16">
            <h2 className="text-3xl font-bold text-white">Still have questions?</h2>
            <p className="mt-4 text-lg text-indigo-100">
              Sign up for free and reach out to our support team at support@getsent.dev. We'll help you get your emails delivered.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                to="/register"
                className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-4 text-base font-semibold text-indigo-600 shadow-lg transition-all hover:bg-indigo-50"
              >
                Get Started Free
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                </svg>
              </Link>
              <a
                href="mailto:support@getsent.dev"
                className="inline-flex items-center gap-2 text-base font-medium text-white/90 transition-colors hover:text-white"
              >
                <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                </svg>
                support@getsent.dev
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
